import { figure, groundRing, leaf, slash, star } from '../kit';
import { type Pix } from '../pix';
import { registerIcons } from '../store';

/**
 * ไม้พลองคลื่นไผ่ (คลื่นไผ่, soft): the bamboo-wave staff. A long bamboo pole with darker nodes, sweeping
 * arcs, and river water in teal crests with foam; a bamboo leaf here and there.
 */

const BAMBOO = '#c8b860';
const NODE = '#7a6a2a';
const WATER = '#3fa8b8';
const FOAM = '#e0fbff';
const LEAF = '#6cc04a';

/** The staff from (x0,y0) to (x1,y1), a node every 5 px. */
function staff(p: Pix, x0: number, y0: number, x1: number, y1: number): void {
  p.thick(x0, y0, x1, y1, BAMBOO, 2);
  const len = Math.hypot(x1 - x0, y1 - y0);
  for (let s = 4; s < len - 1; s += 5) {
    const t = s / len;
    p.px(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t, NODE);
  }
  p.px(x0, y0, NODE).px(x1, y1, NODE);
}

/** A wave crest curling over to the right. */
function crest(p: Pix, x: number, y: number, w: number): void {
  p.ring(x + w / 2, y + w / 2, w / 2, w / 2, WATER, Math.PI, Math.PI * 1.95);
  p.ring(x + w / 2, y + w / 2 + 1, w / 2 - 1, w / 2 - 1, FOAM, Math.PI * 1.1, Math.PI * 1.7);
}

registerIcons('skill', {
  // A wide sweep that throws a wave ahead of it.
  'tt-wave': (p) => {
    slash(p, 16, 22, 14, -Math.PI * 0.95, -Math.PI * 0.2, WATER, 3);
    staff(p, 3, 27, 28, 9);
    crest(p, 18, 18, 9);
  },
  // Low across the ankles: the pole hits two feet out from under.
  'tt-ankle': (p) => {
    p.rect(9, 4, 2, 12, '#a8b4c8').rect(15, 4, 2, 12, '#a8b4c8');
    p.rect(7, 16, 4, 2, '#a8b4c8').rect(15, 16, 4, 2, '#a8b4c8');
    staff(p, 2, 21, 29, 15);
    star(p, 13, 15, 3, FOAM);
  },
  // The staff spun full circle in a whirlpool.
  'tt-whirl': (p) => {
    p.ring(16, 16, 12, 12, WATER, 0, Math.PI * 2);
    slash(p, 16, 16, 10, -Math.PI * 0.9, Math.PI * 0.1, FOAM, 2);
    staff(p, 5, 25, 27, 7);
  },
  // Pole-vault over a wave onto the mark.
  'tt-vault': (p) => {
    crest(p, 4, 18, 11);
    p.rect(2, 27, 28, 2, WATER);
    staff(p, 10, 27, 17, 7);
    figure(p, 17, 3, '#e8f0f0', 'leap');
    p.rect(26, 24, 3, 1, FOAM);
  },
  // Pinned down under the pole's end, three taps.
  'tt-pin': (p) => {
    figure(p, 6, 23, '#a8b4c8', 'sit', {});
    p.rect(4, 29, 18, 1, WATER);
    staff(p, 28, 3, 12, 24);
    for (const [x, y] of [
      [18, 23],
      [21, 20],
      [24, 23],
    ] as const)
      p.rect(x, y, 2, 1, FOAM);
  },
  // Three blows like waves stacked on waves.
  'tt-ripple': (p) => {
    crest(p, 4, 14, 8);
    crest(p, 11, 11, 9);
    crest(p, 19, 8, 10);
    p.rect(3, 22, 26, 1, WATER);
    staff(p, 3, 28, 29, 25);
  },
  // The staff spun into a guard of water before the body.
  'tt-ward': (p) => {
    p.ring(18, 17, 10, 10, WATER, 0, Math.PI * 2);
    p.ring(18, 17, 8, 8, FOAM, -Math.PI * 0.9, -Math.PI * 0.1);
    staff(p, 8, 7, 28, 27);
    staff(p, 28, 7, 8, 27);
    leaf(p, 4, 26, 1, -1, 5, LEAF);
  },
  // Three rings of flood waves round the staff.
  'tt-tide': (p) => {
    groundRing(p, 16, 21, 13, 7, WATER);
    groundRing(p, 16, 21, 9, 5, WATER);
    crest(p, 4, 9, 8);
    crest(p, 20, 9, 8);
    staff(p, 16, 4, 16, 27);
    leaf(p, 13, 5, -1, 0, 4, LEAF);
  },
});
